import React from 'react';
import { SparklesIcon } from '@heroicons/react/16/solid';

// A page or a section that is not ready yet: what it will show, and a way to a part that is ready.
// Left: a "Coming soon" badge, the title, a short text, up to 3 points (icon, title, text) and the actions.
// Right (below the text under the lg width): a faded preview of the coming page, made by the caller from real
// Components with empty values. The preview is decoration: hidden from a screen reader, and no pointer reaches it.
// The Component knows nothing about the feature behind it.

export type ComingSoonPoint = {
	// A 20 px solid icon (heroicons).
	icon: React.ComponentType<React.SVGProps<SVGSVGElement>>,
	title: string,
	text: string,
};

type Props = {
	title: string,
	description: React.ReactNode,
	// Default 'Coming soon'.
	badge?: string,
	points?: ComingSoonPoint[],
	// The buttons, for example a link to a part that is ready.
	actions?: React.ReactNode,
	// The look of the coming page, faded at the bottom. Without it, the text takes the full width.
	preview?: React.ReactNode,
};

const ComingSoon: React.FunctionComponent<Props> = ({ title, description, badge = 'Coming soon', points = [], actions, preview }) => {
	const hasPreview = preview !== undefined && preview !== null;
	return <section aria-label={title} className="overflow-hidden rounded-lg bg-white shadow ring-1 ring-gray-900/5">
		<div className={hasPreview ? 'grid grid-cols-1 lg:grid-cols-5' : ''}>
			<div className={hasPreview ? 'flex flex-col gap-y-6 p-6 sm:p-8 lg:col-span-2' : 'flex max-w-2xl flex-col gap-y-6 p-6 sm:p-8'}>
				<span className="inline-flex items-center gap-x-1.5 self-start rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-600/20">
					<SparklesIcon aria-hidden="true" className="h-3.5 w-3.5" />
					{badge}
				</span>
				<div>
					<h2 className="text-2xl font-semibold tracking-tight text-gray-900">{title}</h2>
					<div className="mt-2 text-sm leading-6 text-gray-600">{description}</div>
				</div>
				{points.length > 0 && <ul className="flex flex-col gap-y-4">
					{points.map(point => <li key={point.title} className="flex gap-x-3">
						<span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-600">
							<point.icon aria-hidden="true" className="h-5 w-5" />
						</span>
						<span className="min-w-0">
							<span className="block text-sm font-semibold text-gray-900">{point.title}</span>
							<span className="block text-sm text-gray-500">{point.text}</span>
						</span>
					</li>)}
				</ul>}
				{actions !== undefined && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
			</div>
			{hasPreview && <div aria-hidden="true"
				className="relative h-64 overflow-hidden border-t border-gray-200 bg-gray-50 sm:h-80 lg:col-span-3 lg:h-auto lg:border-l lg:border-t-0">
				<div className="pointer-events-none select-none p-6 opacity-60 sm:p-8 lg:absolute lg:inset-0">{preview}</div>
				<div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-gray-50 to-transparent" />
			</div>}
		</div>
	</section>;
};

export default ComingSoon;
